import { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  Check,
  FileSearch,
  ClipboardList,
  Users,
  ShieldCheck,
  Clock,
  TrendingUp,
  Settings,
  ChevronRight,
  CheckCircle2,
  Minus,
  Plus,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/e7qtpwyy_Bazaart_2CA00B66-0594-4702-AC94-D9D6D5BB8442%20%281%29.JPEG";
const IMG_HERO = "https://images.pexels.com/photos/7693688/pexels-photo-7693688.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop";
const IMG_SIDE = "https://images.pexels.com/photos/7654450/pexels-photo-7654450.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop";

const useInView = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const WHAT_WE_REVIEW = [
  { label: "Provider credentialing status", icon: ShieldCheck },
  { label: "Recredentialing requirements", icon: Clock },
  { label: "Medicaid and commercial payer enrollment activity", icon: FileSearch },
  { label: "Documentation completeness", icon: ClipboardList },
  { label: "Renewal tracking processes", icon: TrendingUp },
  { label: "Workflow ownership and accountability", icon: Users },
  { label: "Follow-up procedures", icon: CheckCircle2 },
  { label: "Enrollment bottlenecks and delays", icon: Settings },
];

const DELIVERABLES = [
  { title: "Credentialing Status Summary", copy: "A detailed overview of provider status and identified concerns.", icon: ShieldCheck },
  { title: "Missing Items Checklist", copy: "Documentation, renewals, applications, and follow-up items requiring attention.", icon: ClipboardList },
  { title: "Workflow & Ownership Review", copy: "Observations regarding accountability, communication, and process management.", icon: Settings },
  { title: "Process Improvement Recommendations", copy: "Practical recommendations to strengthen your credentialing and enrollment operations.", icon: TrendingUp },
  { title: "Action Plan", copy: "A prioritized roadmap outlining immediate next steps and long-term improvements.", icon: FileSearch },
];

const RIGHT_FOR_YOU = [
  "Have multiple providers to manage",
  "Are experiencing enrollment delays",
  "Have inconsistent renewal processes",
  "Have recently grown or expanded services",
  "Have experienced staffing turnover",
  "Want confidence that credentialing activities are being managed effectively",
];

const STEPS = [
  { num: "1", label: "Schedule a Call", desc: "Connect with our team to discuss your situation." },
  { num: "2", label: "We Review Your Current Status", desc: "Share what you have, we assess where things stand." },
  { num: "3", label: "We Identify Gaps & Risks", desc: "Get clarity on what needs attention." },
  { num: "4", label: "You Receive a Clear Action Plan", desc: "Walk away with prioritized next steps." },
];

const FAQS = [
  { q: "How long does the assessment take?", a: "It depends on the size and complexity of your organization. Most assessments are completed within two to four weeks, depending on provider count and the scope of review required." },
  { q: "Do you only work with Medicaid credentialing?", a: "No. Our assessment covers Medicaid, commercial payer enrollments, credentialing, recredentialing, and the workflows that support all of it. We look at the full picture." },
  { q: "Can you help us fix the issues you find?", a: "Yes. After the assessment, we can provide implementation support to help your team address gaps, improve processes, and build more sustainable credentialing operations." },
  { q: "What size organizations do you work with?", a: "We work with growing practices, multi-provider groups, and larger healthcare organizations. If you have credentialing and enrollment activity to manage, the assessment can provide value regardless of size." },
  { q: "How is pricing determined?", a: "Assessment pricing is based on provider count, organizational complexity, and the scope of review required. The best place to start is with a conversation so we can understand your situation." },
];

const MedicaidCredentialing = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="bg-white text-[#0B1F3A] font-body overflow-x-hidden">
      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#00838A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <a href="/medicaid-credentialing-services" className="flex items-center"><img src={LOGO_URL} alt="RNB Consulting Group" className="h-12 w-auto rounded-lg" /></a>
          <a href="#book"><button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-5 py-2.5 rounded-full hover:scale-105 transition-transform">Get Started</button></a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#00838A]" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-0 md:pt-24">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="pb-16 md:pb-24">
              <FadeIn>
                <span className="inline-block bg-[#00838A]/20 text-[#00838A] text-xs font-accent font-semibold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-6">Credentialing &amp; Enrollment Assessment</span>
              </FadeIn>
              <FadeIn delay={80}>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-[3.4rem] font-black tracking-tighter leading-[1.08] text-white mb-5">
                  Know Exactly Where Your Credentialing Stands.
                </h1>
              </FadeIn>
              <FadeIn delay={120}>
                <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-lg">
                  A comprehensive review of your credentialing and enrollment processes designed to identify risks, gaps, and delays before they impact your revenue or operations.
                </p>
              </FadeIn>
              <FadeIn delay={200}>
                <div className="flex flex-wrap gap-3">
                  <a href="#book">
                    <button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-7 py-3.5 rounded-full hover:scale-105 transition-transform shadow-lg shadow-[#00838A]/25 inline-flex items-center gap-2">
                      Request Assessment Pricing <ArrowRight className="w-4 h-4" />
                    </button>
                  </a>
                  <a href="/credentialing-quick-review">
                    <button className="border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2">
                      Not sure yet? Start with a free review call
                    </button>
                  </a>
                </div>
              </FadeIn>
            </div>
            <FadeIn delay={150}>
              <div className="rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img src={IMG_HERO} alt="Team collaborating on credentialing review" className="w-full h-auto object-cover" />
              </div>
            </FadeIn>
          </div>
        </div>
        <div className="relative -mb-1"><svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full"><path d="M0 80V30C240 60 480 0 720 30C960 60 1200 0 1440 30V80H0Z" fill="white"/></svg></div>
      </section>

      {/* WHAT WE REVIEW */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">What we review</p>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-4">A thorough look at every critical area.</h2>
            <p className="text-[#5A5A5A] text-base leading-relaxed mb-12 max-w-2xl">
              We review the full lifecycle of your credentialing and enrollment activities to identify where things are working, where they{'\u2019'}re not, and what needs to happen next.
            </p>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="bg-[#0B1F3A] rounded-2xl p-8 md:p-10">
              <div className="grid sm:grid-cols-2 gap-4">
                {WHAT_WE_REVIEW.map((item, i) => (
                  <div key={`wr-${i}`} className="flex items-center gap-3 bg-white/5 rounded-lg p-3.5">
                    <item.icon className="w-5 h-5 text-[#00838A] shrink-0" strokeWidth={1.8} />
                    <span className="text-white/85 text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="py-20 md:py-28 bg-[#E8F4F5]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">What you{'\u2019'}ll receive</p>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Clear answers, not vague summaries.</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DELIVERABLES.map((d, i) => (
              <FadeIn key={`del-${i}`} delay={i * 80}>
                <div className="bg-white p-7 rounded-2xl border border-[#00838A]/10 hover:-translate-y-1.5 hover:shadow-xl transition-all h-full group">
                  <div className="w-12 h-12 rounded-xl bg-[#00838A]/8 flex items-center justify-center mb-4 group-hover:bg-[#00838A]/15 transition-colors">
                    <d.icon className="w-6 h-6 text-[#00838A] group-hover:text-[#00838A] transition-colors" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-heading font-bold text-base mb-2">{d.title}</h3>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed">{d.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">How it works</p>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Here{'\u2019'}s how it works.</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <FadeIn key={`step-${i}`} delay={i * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#00838A] text-white font-heading font-black text-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">{s.num}</div>
                  <h3 className="font-heading font-bold text-[#0B1F3A] text-base mb-2">{s.label}</h3>
                  <p className="text-sm text-[#5A5A5A]">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* IS IT RIGHT FOR YOU */}
      <section className="py-20 md:py-28 bg-[#E8F4F5]/25">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-14 items-center">
            <FadeIn>
              <div>
                <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">Is it right for you?</p>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-4">Is the Assessment Right for Your Organization?</h2>
                <p className="text-[#5A5A5A] text-base leading-relaxed mb-8">The assessment is often valuable for organizations that:</p>
                <div className="space-y-3">
                  {RIGHT_FOR_YOU.map((item, i) => (
                    <div key={`rfy-${i}`} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-[#00838A] mt-0.5 shrink-0" />
                      <span className="text-[#0B1F3A] text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-[#00838A]/10">
                <img src={IMG_SIDE} alt="Practice manager reviewing credentialing files" className="w-full h-auto object-cover" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">FAQ</p>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Common questions</h2>
            </div>
          </FadeIn>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <FadeIn key={`faq-${i}`} delay={i * 60}>
                  <div className={`border rounded-xl transition-all ${isOpen ? "border-[#00838A]/20 shadow-md bg-white" : "border-[#00838A]/8 bg-white hover:border-[#00838A]/15"}`}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                      <span className="font-heading font-bold text-[#0B1F3A] text-sm pr-4">{f.q}</span>
                      {isOpen ? <Minus className="w-5 h-5 text-[#00838A] shrink-0" /> : <Plus className="w-5 h-5 text-[#00838A] shrink-0" />}
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-5 pb-5 text-[#5A5A5A] text-sm leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING + CTA */}
      <section id="book" className="py-20 md:py-28 bg-[#0B1F3A]">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">Pricing &amp; scope</p>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-white mb-6">Every organization is different.</h2>
            <div className="space-y-4 text-white/60 text-base leading-relaxed mb-10 max-w-xl mx-auto">
              <p>Assessment pricing is based on provider count, organizational complexity, and the scope of review required.</p>
              <p>The best place to start is with a conversation so we can understand your situation and determine whether a formal assessment makes sense.</p>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/credentialing-quick-review">
                <button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-8 py-4 rounded-full hover:scale-105 transition-transform shadow-lg shadow-[#00838A]/20 inline-flex items-center gap-2">
                  Schedule a Free Review Call <ArrowRight className="w-4 h-4" />
                </button>
              </a>
            </div>
            <p className="text-white/40 text-sm mt-6">We{'\u2019'}ll help you understand where things stand and recommend the most appropriate next steps.</p>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0B1F3A] border-t border-white/5 text-white/60 text-sm py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="RNB Consulting Group" className="h-8 w-auto rounded" /><p className="font-heading font-bold text-white tracking-tight">RNB Consulting Group</p></div>
          <p>&copy; {new Date().getFullYear()} RNB Consulting Group. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default MedicaidCredentialing;
